"use client";

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { CloseIcon } from "@/shared/ui/atoms";
import { getKoreanDateKey, MAINTENANCE_NOTICE_END, MAINTENANCE_NOTICE_START, MAINTENANCE_NOTICE_STORAGE_KEY, shouldShowMaintenanceNotice } from "@/features/home/lib/maintenanceNotice";

/** 공지를 확인한 뒤 기존 관심 진료 선택 모달을 표시한다. */
const MaintenanceNotice = t0 => {
  const $ = _c(113);
  const {
    children
  } = t0;
  const t = useTranslations("maintenanceNotice");
  const [open, setOpen] = useState(null);
  const [hideToday, setHideToday] = useState(false);
  const dialogRef = useRef(null);
  const id = useId();
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      let timer;
      const update = () => {
        const now = Date.now();
        let hiddenDate = null;
        try {
          hiddenDate = window.localStorage.getItem(MAINTENANCE_NOTICE_STORAGE_KEY);
        } catch {}
        setOpen(shouldShowMaintenanceNotice(now, hiddenDate));
        const nextBoundary = now < MAINTENANCE_NOTICE_START ? MAINTENANCE_NOTICE_START : MAINTENANCE_NOTICE_END;
        if (nextBoundary > now) {
          timer = setTimeout(update, nextBoundary - now);
        }
      };
      update();
      return () => clearTimeout(timer);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[2] !== open) {
    t3 = () => {
      if (!open) {
        return;
      }
      const dialog = dialogRef.current;
      const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const overflow = document.body.style.overflow;
      dialog?.showModal();
      document.body.style.overflow = "hidden";
      return () => {
        dialog?.close();
        document.body.style.overflow = overflow;
        if (opener?.isConnected) {
          opener.focus();
        }
      };
    };
    t4 = [open];
    $[2] = open;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] !== hideToday) {
    t5 = () => {
      if (hideToday) {
        try {
          window.localStorage.setItem(MAINTENANCE_NOTICE_STORAGE_KEY, getKoreanDateKey(Date.now()));
        } catch {}
      }
      setOpen(false);
    };
    $[5] = hideToday;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const close = t5;
  if (open === null) {
    return null;
  }
  if (!open) {
    return children;
  }
  const t6 = `${id}-title`;
  const t7 = `${id}-description`;
  let t8;
  let t9;
  if ($[7] !== close) {
    t8 = event => {
      event.preventDefault();
      close();
    };
    t9 = event_0 => {
      if (event_0.target === event_0.currentTarget) {
        close();
      }
    };
    $[7] = close;
    $[8] = t8;
    $[9] = t9;
  } else {
    t8 = $[8];
    t9 = $[9];
  }
  let t10;
  if ($[10] !== t) {
    t10 = t("eyebrow");
    $[10] = t;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t10) {
    t11 = <p className="text-xs font-semibold text-primary-600">{t10}</p>;
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t) {
    t12 = t("close");
    $[14] = t;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <CloseIcon size={20} />;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== close || $[18] !== t12) {
    t14 = <button type="button" aria-label={t12} onClick={close} className="-mr-2 -mt-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500">{t13}</button>;
    $[17] = close;
    $[18] = t12;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== t11 || $[21] !== t14) {
    t15 = <div className="mb-3 flex items-center justify-between gap-3">{t11}{t14}</div>;
    $[20] = t11;
    $[21] = t14;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  const t16 = `${id}-title`;
  let t17;
  if ($[23] !== t) {
    t17 = t("title");
    $[23] = t;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== t16 || $[26] !== t17) {
    t18 = <h2 id={t16} className="text-2xl font-bold leading-8 tracking-tight">{t17}</h2>;
    $[25] = t16;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  const t19 = `${id}-description`;
  let t20;
  if ($[28] !== t) {
    t20 = t("description");
    $[28] = t;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== t19 || $[31] !== t20) {
    t21 = <p id={t19} className="mb-5 mt-3 whitespace-pre-line break-keep text-sm leading-6 text-gray-600">{t20}</p>;
    $[30] = t19;
    $[31] = t20;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== t) {
    t22 = t("schedule");
    $[33] = t;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== t22) {
    t23 = <h3 className="font-semibold">{t22}</h3>;
    $[35] = t22;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] !== t) {
    t24 = t("timezone");
    $[37] = t;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== t24) {
    t25 = <span className="text-gray-500">{t24}</span>;
    $[39] = t24;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  let t26;
  if ($[41] !== t23 || $[42] !== t25) {
    t26 = <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs">{t23}{t25}</div>;
    $[41] = t23;
    $[42] = t25;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = ["first", "second"];
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  let t28;
  if ($[45] !== t) {
    t28 = (t27 as const).map((round, index) => <div key={round} className={index ? "mt-3 flex gap-3 border-t border-primary-200 pt-3" : "flex gap-3"}><span className="mt-0.5 h-fit shrink-0 rounded border border-primary-200 px-1.5 py-1 text-xs text-primary-600">{t(`${round}.label`)}</span><div><p className="text-sm font-semibold">{t(`${round}.date`)}</p><p className="mt-0.5 text-[22px] font-bold leading-8 tracking-tight tabular-nums">{index ? "10:30 \u2013 14:00" : "15:10 \u2013 16:00"}</p></div></div>);
    $[45] = t;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  let t29;
  if ($[47] !== t26 || $[48] !== t28) {
    t29 = <div className="rounded-xl bg-primary-50 p-4">{t26}{t28}</div>;
    $[47] = t26;
    $[48] = t28;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  if ($[50] !== t) {
    t30 = t("impact");
    $[50] = t;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] !== t30) {
    t31 = <p className="mb-3 mt-4 break-keep text-sm font-medium leading-6">{t30}</p>;
    $[52] = t30;
    $[53] = t31;
  } else {
    t31 = $[53];
  }
  let t32;
  if ($[54] !== t) {
    t32 = t("scheduleMayChange");
    $[54] = t;
    $[55] = t32;
  } else {
    t32 = $[55];
  }
  let t33;
  if ($[56] !== t32) {
    t33 = <li>{t32}</li>;
    $[56] = t32;
    $[57] = t33;
  } else {
    t33 = $[57];
  }
  let t34;
  if ($[58] !== t) {
    t34 = t("resume");
    $[58] = t;
    $[59] = t34;
  } else {
    t34 = $[59];
  }
  let t35;
  if ($[60] !== t34) {
    t35 = <li>{t34}</li>;
    $[60] = t34;
    $[61] = t35;
  } else {
    t35 = $[61];
  }
  let t36;
  if ($[62] !== t33 || $[63] !== t35) {
    t36 = <ul className="list-disc space-y-1 pl-3.5 text-xs leading-5 text-gray-500">{t33}{t35}</ul>;
    $[62] = t33;
    $[63] = t35;
    $[64] = t36;
  } else {
    t36 = $[64];
  }
  let t37;
  if ($[65] !== t) {
    t37 = t("apology");
    $[65] = t;
    $[66] = t37;
  } else {
    t37 = $[66];
  }
  let t38;
  if ($[67] !== t37) {
    t38 = <p className="mb-2 whitespace-pre-line">{t37}</p>;
    $[67] = t37;
    $[68] = t38;
  } else {
    t38 = $[68];
  }
  let t39;
  if ($[69] !== t) {
    t39 = t("contact");
    $[69] = t;
    $[70] = t39;
  } else {
    t39 = $[70];
  }
  let t40;
  if ($[71] !== t39) {
    t40 = <span>{t39}</span>;
    $[71] = t39;
    $[72] = t40;
  } else {
    t40 = $[72];
  }
  let t41;
  let t42;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <a href="mailto:cs@legalcare.ai" className="underline-offset-2 hover:underline">cs@legalcare.ai</a>;
    t42 = <a href="tel:01044329325" className="underline-offset-2 hover:underline">010-4432-9325</a>;
    $[73] = t41;
    $[74] = t42;
  } else {
    t41 = $[73];
    t42 = $[74];
  }
  let t43;
  if ($[75] !== t40) {
    t43 = <div className="flex flex-wrap gap-x-3 gap-y-1">{t40}{t41}{t42}</div>;
    $[75] = t40;
    $[76] = t43;
  } else {
    t43 = $[76];
  }
  let t44;
  if ($[77] !== t38 || $[78] !== t43) {
    t44 = <div className="mt-4 border-t border-gray-200 pt-3 text-xs leading-5 text-gray-500">{t38}{t43}</div>;
    $[77] = t38;
    $[78] = t43;
    $[79] = t44;
  } else {
    t44 = $[79];
  }
  let t45;
  if ($[80] !== t15 || $[81] !== t18 || $[82] !== t21 || $[83] !== t29 || $[84] !== t31 || $[85] !== t36 || $[86] !== t44) {
    t45 = <div className="min-h-0 overflow-y-auto overscroll-contain px-5 pb-5 pt-6 sm:px-6">{t15}{t18}{t21}{t29}{t31}{t36}{t44}</div>;
    $[80] = t15;
    $[81] = t18;
    $[82] = t21;
    $[83] = t29;
    $[84] = t31;
    $[85] = t36;
    $[86] = t44;
    $[87] = t45;
  } else {
    t45 = $[87];
  }
  let t46;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = event_1 => setHideToday(event_1.target.checked);
    $[88] = t46;
  } else {
    t46 = $[88];
  }
  let t47;
  if ($[89] !== hideToday) {
    t47 = <input type="checkbox" checked={hideToday} onChange={t46} className="h-4 w-4 shrink-0 accent-primary-600" />;
    $[89] = hideToday;
    $[90] = t47;
  } else {
    t47 = $[90];
  }
  let t48;
  if ($[91] !== t) {
    t48 = t("hideToday");
    $[91] = t;
    $[92] = t48;
  } else {
    t48 = $[92];
  }
  let t49;
  if ($[93] !== t47 || $[94] !== t48) {
    t49 = <label className="flex min-h-11 cursor-pointer items-center gap-2 text-xs leading-5 text-gray-600">{t47}{t48}</label>;
    $[93] = t47;
    $[94] = t48;
    $[95] = t49;
  } else {
    t49 = $[95];
  }
  let t50;
  if ($[96] !== t) {
    t50 = t("confirm");
    $[96] = t;
    $[97] = t50;
  } else {
    t50 = $[97];
  }
  let t51;
  if ($[98] !== close || $[99] !== t50) {
    t51 = <button type="button" onClick={close} className="min-h-11 shrink-0 rounded-lg bg-primary-600 px-6 py-3 text-sm font-semibold text-white hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500">{t50}</button>;
    $[98] = close;
    $[99] = t50;
    $[100] = t51;
  } else {
    t51 = $[100];
  }
  let t52;
  if ($[101] !== t49 || $[102] !== t51) {
    t52 = <footer className="flex shrink-0 items-center justify-between gap-3 bg-gray-100 px-5 py-3 sm:px-6">{t49}{t51}</footer>;
    $[101] = t49;
    $[102] = t51;
    $[103] = t52;
  } else {
    t52 = $[103];
  }
  let t53;
  if ($[104] !== t45 || $[105] !== t52) {
    t53 = <section className="flex max-h-[calc(100dvh-32px)] w-full max-w-[440px] flex-col overflow-hidden rounded-[20px] bg-white shadow-2xl">{t45}{t52}</section>;
    $[104] = t45;
    $[105] = t52;
    $[106] = t53;
  } else {
    t53 = $[106];
  }
  let t54;
  if ($[107] !== t53 || $[108] !== t6 || $[109] !== t7 || $[110] !== t8 || $[111] !== t9) {
    t54 = createPortal(<dialog ref={dialogRef} aria-labelledby={t6} aria-describedby={t7} aria-modal="true" onCancel={t8} onClick={t9} className="fixed inset-0 m-0 h-dvh max-h-none w-screen max-w-none items-center justify-center overflow-hidden border-0 bg-transparent p-4 text-gray-900 backdrop:bg-black/50 open:flex">{t53}</dialog>, document.body);
    $[107] = t53;
    $[108] = t6;
    $[109] = t7;
    $[110] = t8;
    $[111] = t9;
    $[112] = t54;
  } else {
    t54 = $[112];
  }
  return t54;
};
export default MaintenanceNotice;